import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Autocomplete,
  TextField,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Avatar,
  Chip,
  Typography,
} from '@mui/material';
import { AtSign } from 'lucide-mui';
import { shareThunk } from '../../selectedVideoSlice';
import type { GlobalDialogProps } from '../../../../types';
import { RootState } from '../../../../reducers';
import { setAlertMessage } from '../../../dashboard/dashboardSlice';
import { sendEmailThunk } from '../../../app/appSlice';
import { getShareUrl } from '../../../../lib/routingUtils';
import { stringAvatar } from '../../../../lib/textUtils';
import { Member } from '../../../../types';
import emailTemplates from '../../../email/templates';

export const ShareWithTeamModal = ({ onClose }: GlobalDialogProps) => {
  const { selectedVideo, settings, team } = useSelector(
    (state: RootState) => state
  );
  const { file } = selectedVideo;
  const { selectedTeam } = team;
  const fullName = settings?.user?.name || 'Someone';
  const firstName = fullName.split(' ')[0];
  const [selectedMembers, setSelectedMembers] = useState<Member[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  const teamMembers = selectedTeam?.teamSpaceMembers || [];

  const getStatus = (member: Member) => {
    if (!member.inviteAccepted && member.unregistered) {
      return {
        label: 'Unregistered',
        color: 'warning.light',
        textColor: 'warning.dark',
      };
    }
    if (!member.inviteAccepted) {
      return {
        label: 'Pending',
        color: 'warning.light',
        textColor: 'warning.dark',
      };
    }
    if (member.status) {
      return {
        label: 'Active',
        color: 'success.light',
        textColor: 'success.dark',
      };
    }
    return { label: 'Disabled', color: 'error.light', textColor: 'error.dark' };
  };

  const inputStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '28px',
      bgcolor: 'background.paper',
      border: '1px solid',
      borderColor: 'divider',
      minHeight: '48px',
      padding: '4px 8px',
      '& fieldset': {
        border: 'none',
      },
      '& .MuiInputAdornment-root': {
        marginRight: '-6px',
        height: '100%',
      },
      '& .MuiChip-root': {
        borderRadius: '16px',
        height: '32px',
        margin: '4px',
        fontSize: '0.9rem',
      },
      '& .MuiAutocomplete-endAdornment': {
        right: '14px',
      },
    },
    '& .MuiOutlinedInput-input': {
      boxSizing: 'border-box',
      padding: '12px 16px',
      fontSize: '1rem',
      lineHeight: '1.5',
    },
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  const handleShare = async () => {
    if (selectedMembers.length === 0) return;

    try {
      setIsLoading(true);
      const { shareId } = ((await dispatch(
        shareThunk({
          fileId: file._id,
          copyUrlToClipboard: false,
        })
      )) as any).payload;

      if (!shareId) {
        throw new Error('Failed to generate share link');
      }

      const shareUrl = getShareUrl(shareId);

      // Send emails in parallel
      await Promise.all(
        selectedMembers.map((member) =>
          dispatch(
            sendEmailThunk({
              template: 'genericEmail',
              toEmail: member.email,
              data: emailTemplates.shareWithTeam({
                senderName: firstName,
                fileName: file.name,
                shareUrl,
                recipientName: member.name,
              }),
            })
          )
        )
      );

      dispatch(
        setAlertMessage({
          message: `Shared with ${selectedMembers.length} team member${
            selectedMembers.length === 1 ? '' : 's'
          }!`,
          type: 'success',
          buttonText: 'Dismiss',
        })
      );
      handleClose();
    } catch (error) {
      console.error('Share error:', error);
      dispatch(
        setAlertMessage({
          message: 'Failed to share with team members. Please try again.',
          type: 'error',
          buttonText: 'Dismiss',
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '28px',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 4,
          pt: 3,
          pb: 2,
          fontSize: '1.5rem',
          fontWeight: 600,
        }}
      >
        Share with team
      </DialogTitle>
      <DialogContent
        sx={{
          px: 4,
          pb: 4,
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        <Box
          sx={{
            color: 'text.secondary',
            typography: 'body1',
          }}
        >
          Select team members to share &ldquo;{file.name}&rdquo; with.
          They&apos;ll receive an email with a link to watch the recording.
        </Box>
        <Autocomplete
          multiple
          options={teamMembers}
          value={selectedMembers}
          onChange={(event, newValue) => setSelectedMembers(newValue)}
          disabled={isLoading}
          getOptionLabel={(option) => option.name}
          renderOption={(props, option) => (
            <Box
              component="li"
              {...props}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                py: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  flex: 1,
                }}
              >
                <Avatar
                  {...stringAvatar(option.name)}
                  sx={{ width: 32, height: 32, '& span': { color: 'white' } }}
                />
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {option.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.email}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  label={option.role}
                  size="small"
                  sx={{
                    bgcolor: 'action.selected',
                    color: 'text.primary',
                    height: '24px',
                  }}
                />
                <Chip
                  label={getStatus(option).label}
                  size="small"
                  sx={{
                    bgcolor: getStatus(option).color,
                    color: getStatus(option).textColor,
                    height: '24px',
                  }}
                />
              </Box>
            </Box>
          )}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option._id}
                label={option.name}
                sx={{
                  borderRadius: '16px',
                  height: '32px',
                  fontSize: '0.9rem',
                }}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search team members"
              variant="outlined"
              sx={inputStyles}
            />
          )}
          sx={{
            '& .MuiAutocomplete-inputRoot': {
              padding: '4px 8px',
              minHeight: '56px',
            },
          }}
        />
      </DialogContent>
      <DialogActions
        sx={{
          px: 4,
          pb: 3,
          gap: 1,
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            borderRadius: '24px',
            textTransform: 'none',
            px: 3,
            py: 1,
            minWidth: '100px',
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleShare}
          startIcon={<AtSign />}
          disabled={selectedMembers.length === 0 || isLoading}
          color="primary"
          variant="contained"
          sx={{
            borderRadius: '24px',
            textTransform: 'none',
            px: 3,
            py: 1,
            minWidth: '100px',
          }}
        >
          {isLoading ? 'Sharing...' : 'Share'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareWithTeamModal;
